import React, { useEffect, useState } from 'react';
import { XMarkIcon, TruckIcon, ArrowPathIcon, ArrowDownTrayIcon, DocumentTextIcon, DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useCtesDaNfe } from '../../hooks/useXmlTerceiros';
import { useBaixarXmlCte, useGerarDacteCte } from '../../hooks/useCte';
import { money, formatCnpj, formatDateTime } from '../../utils/format';
import { btnSecundario } from '../ui/iris';

interface CtesDaNfeDialogProps {
  nfeId: number | null;
  numeroNfe?: number;
  isOpen: boolean;
  onClose: () => void;
}

const CtesDaNfeDialog: React.FC<CtesDaNfeDialogProps> = ({ nfeId, numeroNfe, isOpen, onClose }) => {
  const { data: ctes, isLoading, error } = useCtesDaNfe(nfeId ?? undefined, isOpen);
  const baixarXml = useBaixarXmlCte();
  const gerarDacte = useGerarDacteCte();
  const [copiadoId, setCopiadoId] = useState<number | null>(null);

  // Fechar com ESC + travar scroll do body enquanto aberto.
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = original;
    };
  }, [isOpen, onClose]);

  const handleCopiar = async (chave: string, id: number) => {
    try {
      await navigator.clipboard.writeText(chave);
      setCopiadoId(id);
      setTimeout(() => setCopiadoId((cur) => (cur === id ? null : cur)), 1500);
    } catch { /* silencioso */ }
  };

  if (!isOpen || !nfeId) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" onClick={onClose} />

      {/* Mobile: full-screen. sm+: card centralizado. */}
      <div className="fixed inset-0 flex flex-col sm:items-center sm:justify-center sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="CT-e que referenciam esta NF-e"
          className="relative flex h-full w-full flex-col overflow-hidden bg-surface sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-iris-lg sm:border sm:border-line-strong sm:shadow-iris-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris bg-accent">
                <TruckIcon className="h-4 w-4 text-white" />
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-tx">CT-e desta NF-e</h2>
                <p className="truncate text-[11px] text-tx-3">{numeroNfe ? `NF-e ${numeroNfe} · transporte vinculado` : 'Transporte vinculado'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="flex-1 space-y-2.5 overflow-y-auto p-4 sm:p-5">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-tx-3">
                <ArrowPathIcon className="h-5 w-5 animate-spin" /> Buscando CT-e vinculados…
              </div>
            )}

            {error && (
              <div className="rounded-iris border border-danger-border bg-danger-soft p-3 text-sm text-danger">
                Não foi possível buscar os CT-e vinculados.
              </div>
            )}

            {!isLoading && !error && ctes && ctes.length === 0 && (
              <div className="rounded-iris border border-line bg-surface-2 px-6 py-10 text-center">
                <TruckIcon className="mx-auto h-9 w-9 text-tx-3" />
                <p className="mt-3 text-sm font-medium text-tx">Nenhum CT-e referencia esta NF-e</p>
                <p className="mt-1 text-[13px] text-tx-3">Só aparecem aqui os CT-e baixados da SEFAZ que citam a chave desta nota como carga transportada.</p>
              </div>
            )}

            {!isLoading && !error && ctes && ctes.map((cte) => (
              <div key={cte.id} className="rounded-iris border border-line bg-surface-2 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-tx">CT-e {cte.numeroCte || 'S/N'}</span>
                    {cte.serieCte && <span className="text-[11px] text-tx-3">Série {cte.serieCte}</span>}
                  </div>
                  {cte.situacaoDocumento && (
                    <span className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tx-3">{cte.situacaoDocumento}</span>
                  )}
                </div>

                {/* Transportadora (emitente do CT-e) */}
                <div className="mt-2 flex items-center gap-2 text-[13px] text-tx">
                  <TruckIcon className="h-4 w-4 flex-shrink-0 text-tx-3" />
                  <span className="truncate font-medium" title={cte.nomeEmitente ?? undefined}>{cte.nomeEmitente || formatCnpj(cte.cnpjEmitente)}</span>
                </div>

                {/* Remetente → Destinatário */}
                <div className="mt-1.5 grid grid-cols-2 gap-2 text-[13px]">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-tx-3">Remetente</p>
                    <p className="truncate text-tx-2" title={cte.nomeRemetente ?? undefined}>{cte.nomeRemetente || '—'}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-tx-3">Destinatário</p>
                    <p className="truncate text-tx-2" title={cte.nomeDestinatario ?? undefined}>{cte.nomeDestinatario || '—'}</p>
                  </div>
                </div>

                {/* Meta: emissão · valor · frete · CIF/FOB */}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-2 text-[13px]">
                  <span className="tabular-nums text-tx-3">{formatDateTime(cte.dataEmissao)}</span>
                  {cte.valorTotal != null && (
                    <span><span className="text-tx-3">Valor </span><span className="font-medium tabular-nums text-tx">{money(cte.valorTotal)}</span></span>
                  )}
                  {cte.valorFrete != null && (
                    <span><span className="text-tx-3">Frete </span><span className="font-medium tabular-nums text-tx">{money(cte.valorFrete)}</span></span>
                  )}
                  {cte.tipoPagamento && (
                    <span className="rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium text-tx-2">{cte.tipoPagamento}</span>
                  )}
                </div>

                {/* Chave + copiar */}
                <div className="mt-2 flex items-center gap-2">
                  <DocumentTextIcon className="h-3.5 w-3.5 flex-shrink-0 text-tx-3" />
                  <span className="min-w-0 flex-1 truncate font-mono text-[11px] tabular-nums text-tx-3">{cte.chaveAcesso}</span>
                  <button
                    onClick={() => handleCopiar(cte.chaveAcesso, cte.id)}
                    className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface hover:text-tx"
                    title="Copiar chave do CT-e"
                    aria-label="Copiar chave do CT-e"
                  >
                    {copiadoId === cte.id ? <CheckIcon className="h-4 w-4 text-ok" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                  </button>
                </div>

                {/* Downloads do CT-e (reusa os endpoints do módulo CT-e por id do cte_documento) */}
                <div className="mt-2 flex gap-2 border-t border-line pt-2">
                  <button
                    onClick={() => gerarDacte.mutate(cte.id)}
                    disabled={gerarDacte.isPending}
                    className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-iris border border-line bg-surface text-[13px] font-medium text-tx transition-colors hover:bg-surface-2 disabled:opacity-50"
                    title="Baixar DACTE (PDF)"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 text-tx-2" /> DACTE
                  </button>
                  <button
                    onClick={() => baixarXml.mutate(cte.id)}
                    disabled={baixarXml.isPending}
                    className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-iris border border-line bg-surface text-[13px] font-medium text-tx transition-colors hover:bg-surface-2 disabled:opacity-50"
                    title="Baixar XML do CT-e"
                  >
                    <DocumentTextIcon className="h-4 w-4 text-tx-2" /> XML
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex justify-end border-t border-line bg-surface-2 px-4 py-3">
            <button onClick={onClose} className={btnSecundario}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CtesDaNfeDialog;
